'use client';
import { useResource } from '@/lib/use-resource';

interface ImpactRow {
  symbol: string;
  reportDate: string;
  period: string;
  epsActual: number | null;
  epsEstimate: number | null;
  surprisePct: number | null;
  preDate: string | null;
  postDate: string | null;
  preComposite: number | null;
  postComposite: number | null;
  preQ: number | null;
  postQ: number | null;
  preV: number | null;
  postV: number | null;
  deltaComposite: number | null;
  deltaQ: number | null;
  deltaV: number | null;
}

interface ImpactFile {
  asOf: string;
  lookbackDays: number;
  rows: ImpactRow[];
}

const fmtSurp = (v: number | null) => {
  if (v == null) return '—';
  // Cap absurd surprise % (small-denominator artifacts) at ±999%
  if (Math.abs(v) > 999) return `${v >= 0 ? '+' : '−'}>999%`;
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};
const fmtDelta = (v: number | null) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
const fmtScore = (v: number | null) =>
  v == null ? '—' : v.toFixed(2);

export function EarningsImpactTable({ dataUrl = '/api/earnings-impact' }: { dataUrl?: string } = {}) {
  const { data } = useResource<ImpactFile>(dataUrl);

  if (!data) return null;
  if (data.rows.length === 0) return null;

  const withDelta = data.rows.filter((r) => r.deltaComposite != null);

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">
          Earnings Impact
        </h2>
        <div className="text-[10px] text-[#64748b]">
          {data.rows.length} portfolio reports · last {data.lookbackDays}d ·{' '}
          {withDelta.length} with full pre/post Δ · pre-earnings snapshot vs first refresh after
        </div>
      </div>
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[repeat(12,minmax(0,1fr))] gap-1 px-2 py-2 text-[9px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]">
          <div className="col-span-1">Sym</div>
          <div className="col-span-2">Report</div>
          <div className="col-span-2 text-right">Surprise</div>
          <div className="col-span-1 text-right">Pre Comp</div>
          <div className="col-span-1 text-right">Post Comp</div>
          <div className="col-span-1 text-right text-amber-300">ΔComp</div>
          <div className="col-span-1 text-right">ΔQ</div>
          <div className="col-span-1 text-right">ΔV</div>
          <div className="col-span-2 text-[9px] normal-case tracking-normal">Period</div>
        </div>
        {data.rows.map((r) => {
          const dC = r.deltaComposite;
          const dColor =
            dC == null ? 'text-[#64748b]' : dC > 0.1 ? 'text-emerald-400' : dC < -0.1 ? 'text-red-400' : 'text-[#cbd5e1]';
          const surpColor =
            r.surprisePct == null ? 'text-[#64748b]' : r.surprisePct > 0 ? 'text-emerald-400' : r.surprisePct < 0 ? 'text-red-400' : 'text-[#cbd5e1]';
          return (
            <div
              key={`${r.symbol}-${r.reportDate}`}
              className="grid grid-cols-[repeat(12,minmax(0,1fr))] gap-1 px-2 py-0.5 text-[11px] leading-tight items-center border-b border-[#1e293b]/60 hover:bg-[#1e293b]/30"
            >
              <div className="col-span-1 font-mono font-bold text-white">{r.symbol}</div>
              <div className="col-span-2 text-[10px] text-[#94a3b8] tabular-nums">{r.reportDate}</div>
              <div className={`col-span-2 text-right tabular-nums font-semibold ${surpColor}`}>
                {fmtSurp(r.surprisePct)}
              </div>
              <div className="col-span-1 text-right tabular-nums text-[#94a3b8]">
                {fmtScore(r.preComposite)}
              </div>
              <div className="col-span-1 text-right tabular-nums text-[#cbd5e1]">
                {fmtScore(r.postComposite)}
              </div>
              <div className={`col-span-1 text-right tabular-nums font-bold ${dColor}`}>
                {fmtDelta(r.deltaComposite)}
              </div>
              <div className={`col-span-1 text-right tabular-nums text-[10px] ${r.deltaQ == null ? 'text-[#64748b]' : r.deltaQ > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {fmtDelta(r.deltaQ)}
              </div>
              <div className={`col-span-1 text-right tabular-nums text-[10px] ${r.deltaV == null ? 'text-[#64748b]' : r.deltaV > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {fmtDelta(r.deltaV)}
              </div>
              <div className="col-span-2 text-[10px] text-[#64748b] tabular-nums truncate">{r.period}</div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-[#64748b] mt-2 leading-relaxed">
        Pre-earnings composite = closest snapshot at-or-before the report date. Post-earnings = first refresh after.
        Δ captures both the trailing-actual revision (Q-side) and forward-consensus rerate (V-side).
        Older reports show post-only because daily snapshots only retain 90 days; the column fills in over time.
      </div>
    </section>
  );
}
